import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: var(--section-max-width);
  width: 100%;

  .inner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 28px;

    // Prevent container from jumping when switching jobs
    @media (min-width: 700px) {
      min-height: 340px;
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: max-content;
  max-width: 100%;
  padding: 0;
  margin: 0;
  align-self: stretch;

  @media (max-width: 480px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    mask-image: linear-gradient(to right, #000 88%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, #000 88%, transparent 100%);
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  padding: 0 20px 2px;
  border: 0;
  border-left: 2px solid var(--surface-muted);
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--accent)' : 'var(--text-secondary)')};
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  text-align: left;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 0 15px 2px;
  }

  &:hover,
  &:focus {
    background-color: var(--surface-raised);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 2px;
  height: var(--tab-height);
  border-radius: var(--border-radius);
  background: var(--accent);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * var(--tab-height)));
  transition: transform 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0.1s;
`;

const StyledTabPanels = styled.div`
  position: relative;
  align-self: stretch;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const StyledTabPanel = styled.div`
  width: 100%;
  max-width: none;
  height: auto;
  padding: 10px 0;

  /* Markdown body: sits under the job title (h3); use h4–h6 only — no h1–h2 in content. */
  h4 {
    margin: 0 0 12px;
    font-size: clamp(var(--fz-md), 1.65vw, var(--fz-lg));
    font-family: var(--font-mono);
    font-weight: 600;
    color: var(--heading-accent);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    line-height: 1.3;

    &:not(:first-child) {
      margin-top: 28px;
    }
  }

  h5 {
    margin: 22px 0 10px;
    font-size: var(--fz-sm);
    font-family: var(--font-mono);
    font-weight: 600;
    color: var(--text-bright);
    letter-spacing: 0.04em;
    line-height: 1.35;
  }

  h6 {
    margin: 18px 0 8px;
    font-size: var(--fz-xs);
    font-family: var(--font-mono);
    font-weight: 600;
    color: var(--text-muted);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.35;
  }

  p {
    margin: 0 0 18px;
    font-size: var(--fz-md);
    line-height: 1.55;
    color: var(--text-secondary);

    & + ul {
      margin-top: 4px;
    }
  }

  ul {
    ${({ theme }) => theme.mixins.fancyList};

    ul {
      margin-top: 10px;
      margin-bottom: 4px;
      padding-left: 4px;

      li {
        font-size: var(--fz-md);
        line-height: 1.5;
        margin-bottom: 8px;

        &:before {
          content: '▹';
          font-size: 0.85em;
          opacity: 0.85;
        }
      }
    }
  }

  h3 {
    margin-bottom: 2px;
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;

    .company {
      color: var(--accent);
    }
  }

  .range {
    margin-bottom: 25px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || !sr) {
      return undefined;
    }

    const el = revealContainer.current;
    if (el) {
      sr.reveal(el, srConfig());
    }

    return () => {
      if (sr && el) {
        sr.clean(el);
      }
    };
  }, [prefersReducedMotion]);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    // If we're at the end, go to the start
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    // If we're at the start, move to the end
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  // Only re-run the effect if tabFocus changes
  useEffect(() => focusTab(), [tabFocus]);

  // Focus on tabs when using up & down arrow keys (vertical tab list)
  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP:
      case KEY_CODES.ARROW_UP_IE11: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }

      case KEY_CODES.ARROW_DOWN:
      case KEY_CODES.ARROW_DOWN_IE11: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I’ve Worked</h2>

      <div className="inner">
        <StyledTabList role="tablist" aria-label="Job tabs" onKeyDown={e => onKeyDown(e)}>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { company } = node.frontmatter;
              return (
                <StyledTabButton
                  key={i}
                  isActive={activeTabId === i}
                  onClick={() => setActiveTabId(i)}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i ? true : false}
                  aria-controls={`panel-${i}`}>
                  <span>{company}</span>
                </StyledTabButton>
              );
            })}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabPanels>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, url, company, range } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  <StyledTabPanel
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}>
                    <h3>
                      <span>{title}</span>
                      <span className="company">
                        &nbsp;@&nbsp;
                        <a href={url} className="inline-link">
                          {company}
                        </a>
                      </span>
                    </h3>

                    <p className="range">{range}</p>

                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </StyledTabPanel>
                </CSSTransition>
              );
            })}
        </StyledTabPanels>
      </div>
    </StyledJobsSection>
  );
};

export default Jobs;
